import type { ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatAccessMode, ChatRank, CrowdBanSummary, ShowAudienceMemberSummary } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { ConfirmModal } from '@fetmode/ui';
import { formatUsername } from '@fetmode/utils';

const AUDIENCE_POLL_MS = 5_000;

export interface RoomAudiencePanelProps {
  showId: string;
  onViewerCountChange?: (count: number) => void;
  onMessage?: (fan: { userId: string; username: string }) => void;
}

function roleLabel(role: ShowAudienceMemberSummary['role']): string {
  switch (role) {
    case 'PARTICIPANT':
      return t('common.studio.live.audienceRoleParticipant');
    case 'SPY':
      return t('common.studio.live.audienceRoleSpy');
    default:
      return t('common.studio.live.audienceRoleViewer');
  }
}

function audienceFingerprint(
  members: ShowAudienceMemberSummary[],
  memberCount: number,
  guestCount: number,
  viewerCount: number,
  grayChatMuted: boolean,
  crowdBan: CrowdBanSummary | null,
): string {
  return [
    String(memberCount),
    String(guestCount),
    String(viewerCount),
    grayChatMuted ? '1' : '0',
    crowdBan ? `${crowdBan.roundId}:${crowdBan.status}:${String(crowdBan.voteScore)}` : '',
    ...members.map(
      (member) =>
        `${member.userId}:${member.role}:${member.hasPaidEntry ? '1' : '0'}:${member.username}:${member.holdUntil ?? ''}:${member.mutedUntil ?? ''}:${member.rank}:${String(member.kingSlot ?? '')}:${member.isModerator ? '1' : '0'}:${String(member.queuePosition ?? '')}`,
    ),
  ].join('|');
}

type AudienceConfirm = {
  message: string;
  confirmLabel: string;
  confirmVariant?: 'primary' | 'request';
  run: () => Promise<void>;
};

function RankPip({ rank, kingSlot, isModerator }: { rank: ChatRank; kingSlot: 1 | 2 | 3 | null; isModerator: boolean }): ReactElement {
  return (
    <span className="live-controls__audience-badges">
      {kingSlot ? (
        <span className="live-controls__audience-badge live-controls__audience-badge--king">
          {t('common.studio.live.audienceKing').replace('{{slot}}', String(kingSlot))}
        </span>
      ) : null}
      <span
        className={`live-controls__audience-pip live-controls__audience-pip--${rank.toLowerCase()}`}
        title={rank}
      />
      {isModerator ? (
        <span className="live-controls__audience-badge live-controls__audience-badge--mod">
          {t('common.studio.live.audienceMod')}
        </span>
      ) : null}
    </span>
  );
}

export function RoomAudiencePanel({
  showId,
  onViewerCountChange,
  onMessage,
}: RoomAudiencePanelProps): ReactElement {
  const { api } = useAuth();
  const [members, setMembers] = useState<ShowAudienceMemberSummary[]>([]);
  const [memberCount, setMemberCount] = useState(0);
  const [guestCount, setGuestCount] = useState(0);
  const [spyCount, setSpyCount] = useState(0);
  const [viewerCount, setViewerCount] = useState(0);
  const [grayChatMuted, setGrayChatMuted] = useState(false);
  const [unbanPriceCoins, setUnbanPriceCoins] = useState<number | null>(500);
  const [unbanDraft, setUnbanDraft] = useState('500');
  const [crowdBan, setCrowdBan] = useState<CrowdBanSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [menuUserId, setMenuUserId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<AudienceConfirm | null>(null);
  const previousNeverMode = useRef<ChatAccessMode>('ALLOWED');
  const onViewerCountChangeRef = useRef(onViewerCountChange);
  onViewerCountChangeRef.current = onViewerCountChange;
  const fingerprintRef = useRef<string>('');
  const hasLoadedRef = useRef(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const applyAudience = useCallback(
    (result: {
      members: ShowAudienceMemberSummary[];
      memberCount: number;
      guestCount: number;
      spyCount: number;
      viewerCount: number;
      grayChatMuted: boolean;
      unbanPriceCoins: number | null;
      crowdBan: CrowdBanSummary | null;
    }) => {
      const nextFingerprint = audienceFingerprint(
        result.members,
        result.memberCount,
        result.guestCount,
        result.viewerCount,
        result.grayChatMuted,
        result.crowdBan,
      );
      if (nextFingerprint === fingerprintRef.current) {
        return;
      }
      fingerprintRef.current = nextFingerprint;
      setMembers(result.members);
      setMemberCount(result.memberCount);
      setGuestCount(result.guestCount);
      setSpyCount(result.spyCount);
      setViewerCount(result.viewerCount);
      setGrayChatMuted(result.grayChatMuted);
      setUnbanPriceCoins(result.unbanPriceCoins);
      setUnbanDraft(result.unbanPriceCoins !== null ? String(result.unbanPriceCoins) : '500');
      setCrowdBan(result.crowdBan);
      onViewerCountChangeRef.current?.(result.viewerCount);
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    hasLoadedRef.current = false;
    fingerprintRef.current = '';
    setIsLoading(true);
    setMembers([]);
    setMemberCount(0);
    setGuestCount(0);
    setSpyCount(0);
    setViewerCount(0);
    setMenuUserId(null);

    const loadAudience = async (): Promise<void> => {
      const result = await api.listShowAudience(showId);
      if (cancelled) {
        return;
      }
      applyAudience(result);
      hasLoadedRef.current = true;
      setIsLoading(false);
    };

    void loadAudience().catch(() => {
      if (!cancelled) {
        setIsLoading(false);
      }
    });

    const timer = window.setInterval(() => {
      void api
        .listShowAudience(showId)
        .then((result) => {
          if (!cancelled) {
            applyAudience(result);
            if (!hasLoadedRef.current) {
              hasLoadedRef.current = true;
              setIsLoading(false);
            }
          }
        })
        .catch(() => undefined);
    }, AUDIENCE_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [api, applyAudience, showId]);

  useEffect(() => {
    if (!menuUserId) {
      return;
    }
    const onPointerDown = (event: PointerEvent): void => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuUserId(null);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [menuUserId]);

  const runAction = async (work: () => Promise<void>): Promise<void> => {
    setIsBusy(true);
    setActionError(null);
    try {
      await work();
      setMenuUserId(null);
      const result = await api.listShowAudience(showId);
      applyAudience(result);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Unable to complete that action.');
    } finally {
      setIsBusy(false);
    }
  };

  const toggleGrayMute = async (): Promise<void> => {
    await runAction(async () => {
      const result = await api.getModelChatConfig();
      const policy = result.config.chatAccessPolicy;
      const current = policy.rules.NEVER_PURCHASED.mode;
      const nextMode: ChatAccessMode =
        current === 'DISABLED'
          ? previousNeverMode.current === 'DISABLED'
            ? 'ALLOWED'
            : previousNeverMode.current
          : 'DISABLED';
      if (current !== 'DISABLED') {
        previousNeverMode.current = current;
      }
      await api.updateModelChatConfig({
        chatAccessPolicy: {
          ...policy,
          rules: {
            ...policy.rules,
            NEVER_PURCHASED: { ...policy.rules.NEVER_PURCHASED, mode: nextMode },
          },
        },
      });
    });
  };

  const saveUnbanPrice = async (coins: number | null): Promise<void> => {
    await runAction(async () => {
      await api.updateUnbanPrice({ coins });
    });
  };

  return (
    <div className="fm-card live-controls__audience-panel">
      <h2 className="live-controls__section-title" style={{ marginTop: 0 }}>
        {t('common.studio.live.audienceTitle')}
      </h2>
      <p style={{ margin: '0 0 0.75rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.live.audienceSummary')
          .replace('{{total}}', String(viewerCount))
          .replace('{{members}}', String(memberCount))
          .replace('{{guests}}', String(guestCount))}
        {spyCount > 0
          ? ` · ${t('common.studio.live.audienceSpies').replace('{{count}}', String(spyCount))}`
          : ''}
      </p>

      <label className="live-controls__audience-toggle">
        <input
          type="checkbox"
          checked={grayChatMuted}
          disabled={isBusy}
          onChange={() => {
            void toggleGrayMute();
          }}
        />
        {t('common.studio.live.grayMuteToggle')}
      </label>

      <div className="live-controls__audience-unban">
        <label>
          {t('common.studio.live.unbanPriceLabel')}
          <input
            className="fm-input"
            type="number"
            min={50}
            max={50000}
            value={unbanDraft}
            disabled={isBusy || unbanPriceCoins === null}
            onChange={(event) => {
              setUnbanDraft(event.target.value);
            }}
          />
        </label>
        <button
          type="button"
          className="live-controls__audience-text-btn"
          disabled={isBusy}
          onClick={() => {
            const parsed = Number.parseInt(unbanDraft, 10);
            if (Number.isFinite(parsed)) {
              void saveUnbanPrice(parsed);
            }
          }}
        >
          {t('common.studio.live.unbanPriceSave')}
        </button>
        <button
          type="button"
          className="live-controls__audience-text-btn"
          disabled={isBusy}
          onClick={() => {
            void saveUnbanPrice(unbanPriceCoins === null ? 500 : null);
          }}
        >
          {unbanPriceCoins === null
            ? t('common.studio.live.unbanPriceSave')
            : t('common.studio.live.unbanPriceOff')}
        </button>
      </div>

      {crowdBan ? (
        <div className="live-controls__crowd-ban">
          <p>
            {t('common.studio.live.crowdBanTitle').replace(
              '{{username}}',
              crowdBan.targetUsername,
            )}
            {' · '}
            {crowdBan.status === 'GRACE'
              ? t('common.studio.live.crowdBanGrace')
              : t('common.studio.live.crowdBanVoting')}
            {' · '}
            {t('common.studio.live.crowdBanScore')
              .replace('{{score}}', String(crowdBan.voteScore))
              .replace('{{threshold}}', String(crowdBan.threshold))}
          </p>
          <div className="live-controls__crowd-ban-actions">
            <button
              type="button"
              disabled={isBusy}
              onClick={() => {
                void runAction(async () => {
                  await api.resolveCrowdBan(showId, crowdBan.roundId, { decision: 'APPROVE' });
                });
              }}
            >
              {t('common.studio.live.crowdBanApprove')}
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => {
                void runAction(async () => {
                  await api.resolveCrowdBan(showId, crowdBan.roundId, { decision: 'DENY' });
                });
              }}
            >
              {t('common.studio.live.crowdBanDeny')}
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => {
                void runAction(async () => {
                  await api.resolveCrowdBan(showId, crowdBan.roundId, { decision: 'IGNORE' });
                });
              }}
            >
              {t('common.studio.live.crowdBanIgnore')}
            </button>
          </div>
        </div>
      ) : null}

      {actionError ? <p className="fm-error">{actionError}</p> : null}

      {isLoading && members.length === 0 ? (
        <p className="live-controls__muted">{t('common.studio.live.audienceLoading')}</p>
      ) : null}

      {!isLoading && members.length === 0 && guestCount === 0 ? (
        <p className="live-controls__muted">{t('common.studio.live.audienceEmpty')}</p>
      ) : null}

      {members.length > 0 ? (
        <ul className="live-controls__audience-list">
          {members.map((member) => (
            <li key={member.userId} className="live-controls__audience-row">
              <span className="live-controls__audience-identity">
                <RankPip rank={member.rank} kingSlot={member.kingSlot} isModerator={member.isModerator} />
                <strong>{formatUsername(member.username)}</strong>
                {member.hasPaidEntry ? (
                  <span className="live-controls__audience-paid">{t('common.studio.live.audiencePaid')}</span>
                ) : null}
                {member.mutedUntil ? (
                  <span className="live-controls__audience-muted">{t('common.studio.live.audienceMuted')}</span>
                ) : null}
                {member.queuePosition ? (
                  <span className="live-controls__audience-queue">
                    {t('common.studio.live.audienceQueued').replace(
                      '{{position}}',
                      String(member.queuePosition),
                    )}
                  </span>
                ) : null}
              </span>
              <span className="live-controls__audience-meta">
                {member.holdUntil
                  ? t('common.studio.live.audienceHolding')
                  : roleLabel(member.role)}
                <div className="live-controls__audience-menu-wrap" ref={menuUserId === member.userId ? menuRef : undefined}>
                  <button
                    type="button"
                    className="live-controls__audience-menu-btn"
                    aria-label={t('common.studio.live.audienceMenu')}
                    disabled={isBusy}
                    onClick={() => {
                      setMenuUserId((current) => (current === member.userId ? null : member.userId));
                    }}
                  >
                    ⋯
                  </button>
                  {menuUserId === member.userId ? (
                    <div className="live-controls__audience-menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenuUserId(null);
                          setPendingConfirm({
                            message: t('common.studio.live.audienceMuteConfirm').replace(
                              '{{username}}',
                              member.username,
                            ),
                            confirmLabel: t('common.studio.live.audienceMute'),
                            run: async () => {
                              await api.muteShowUser(showId, { userId: member.userId });
                            },
                          });
                        }}
                      >
                        {t('common.studio.live.audienceMute')}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenuUserId(null);
                          setPendingConfirm({
                            message: t('common.studio.live.audienceKickConfirm').replace(
                              '{{username}}',
                              member.username,
                            ),
                            confirmLabel: t('common.studio.live.audienceKick'),
                            run: async () => {
                              await api.kickShowUser(showId, { userId: member.userId });
                            },
                          });
                        }}
                      >
                        {t('common.studio.live.audienceKick')}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenuUserId(null);
                          setPendingConfirm({
                            message: t('common.studio.live.audienceBanConfirm').replace(
                              '{{username}}',
                              member.username,
                            ),
                            confirmLabel: t('common.studio.live.audienceBan'),
                            confirmVariant: 'request',
                            run: async () => {
                              await api.banShowUser(showId, { userId: member.userId });
                            },
                          });
                        }}
                      >
                        {t('common.studio.live.audienceBan')}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenuUserId(null);
                          setPendingConfirm({
                            message: t('common.studio.live.audienceBlockConfirm').replace(
                              '{{username}}',
                              member.username,
                            ),
                            confirmLabel: t('common.studio.live.audienceBlock'),
                            confirmVariant: 'request',
                            run: async () => {
                              await api.blockUser({ blockedUserId: member.userId });
                            },
                          });
                        }}
                      >
                        {t('common.studio.live.audienceBlock')}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenuUserId(null);
                          setPendingConfirm({
                            message: t('common.studio.live.audienceReportConfirm').replace(
                              '{{username}}',
                              member.username,
                            ),
                            confirmLabel: t('common.studio.live.audienceReport'),
                            confirmVariant: 'request',
                            run: async () => {
                              await api.createReport({
                                reportedUserId: member.userId,
                                category: 'HARASSMENT',
                                showId,
                              });
                            },
                          });
                        }}
                      >
                        {t('common.studio.live.audienceReport')}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          onMessage?.({ userId: member.userId, username: member.username });
                          setMenuUserId(null);
                        }}
                      >
                        {t('common.studio.live.audienceMessage')}
                      </button>
                      {member.queuePosition === 1 ? (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            void runAction(async () => {
                              await api.startNextPrivateQueue(showId);
                            });
                          }}
                        >
                          {t('common.studio.live.audienceCallNext')}
                        </button>
                      ) : null}
                      {member.queueEntryId ? (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            void runAction(async () => {
                              await api.skipPrivateQueueEntry(showId, member.queueEntryId ?? '');
                            });
                          }}
                        >
                          {t('common.studio.live.audienceSkipQueue')}
                        </button>
                      ) : null}
                      {member.isModerator ? (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setMenuUserId(null);
                            setPendingConfirm({
                              message: t('common.studio.live.audienceRemoveModConfirm').replace(
                                '{{username}}',
                                member.username,
                              ),
                              confirmLabel: t('common.studio.live.audienceRemoveMod'),
                              confirmVariant: 'request',
                              run: async () => {
                                await api.removeModerator(member.userId);
                              },
                            });
                          }}
                        >
                          {t('common.studio.live.audienceRemoveMod')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setMenuUserId(null);
                            setPendingConfirm({
                              message: t('common.studio.live.audienceAssignModConfirm').replace(
                                '{{username}}',
                                member.username,
                              ),
                              confirmLabel: t('common.studio.live.audienceAssignMod'),
                              run: async () => {
                                await api.assignModerator({ userId: member.userId });
                              },
                            });
                          }}
                        >
                          {t('common.studio.live.audienceAssignMod')}
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {guestCount > 0 ? (
        <p
          style={{
            margin: members.length > 0 ? '0.75rem 0 0' : 0,
            fontSize: '0.875rem',
            color: 'var(--fm-color-text-muted)',
          }}
        >
          {t('common.studio.live.audienceGuests').replace('{{count}}', String(guestCount))}
        </p>
      ) : null}

      <ConfirmModal
        open={pendingConfirm !== null}
        title={t('common.dialog.title')}
        message={pendingConfirm?.message ?? ''}
        confirmLabel={pendingConfirm?.confirmLabel ?? t('common.dialog.confirm')}
        cancelLabel={t('common.dialog.cancel')}
        confirmVariant={pendingConfirm?.confirmVariant ?? 'primary'}
        busy={isBusy}
        onCancel={() => {
          if (!isBusy) {
            setPendingConfirm(null);
          }
        }}
        onConfirm={() => {
          if (!pendingConfirm || isBusy) {
            return;
          }
          const { run } = pendingConfirm;
          setPendingConfirm(null);
          void runAction(run);
        }}
      />
    </div>
  );
}
